import { Card, Checkbox, IconButton, ListItemIcon, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';

declare global {
  interface Window {
    projectId: string;
  }
}

import MoreVertIcon from '@mui/icons-material/MoreVert';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import { downloadProjectAsBpsFile } from '../../utils/projectDownload';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Project } from './projects';
import { useDispatch, useSelector } from 'react-redux';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { ApplicationActions } from '../../utils/constants';
import { projectSlice } from '../../redux/ProjectSlice';
import { RootState } from '../../redux/Store.js';
import ShareCodeUpload from '@mui/icons-material/CloudUpload';
import ShareCodeInfo from '@mui/icons-material/CloudCircle';
import { duplicateProject } from '../../utils/projectDuplication';

interface ProjectCardProps {
  project: Project;
  isSelected: boolean;
  handleProjectSelect: () => void;
  handleShiftProjectSelect: (e: React.MouseEvent) => void;
}

const ProjectCard = ({ project, handleProjectSelect, handleShiftProjectSelect, isSelected }: ProjectCardProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { isMultiSelect } = useSelector((state: RootState) => state.project.projectSelect);
  const { DELETE_PROJECT } = ApplicationActions.Dialogs;
  const { DRIVEBASE_DATA } = ApplicationActions.Tabs;

  const dispatch = useDispatch();

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenEditProgramDialog = () => {
    const dialog = {
      ...ApplicationActions.Dialogs.PROJECT_NAME,
      options: {
        ...ApplicationActions.Dialogs.PROJECT_NAME.options,
        isEditing: true,
        projectId: project.id,
        title: 'projectNameDialog.edit.title',
      },
    };
    dispatch(applicationSlice.actions.onOpenDialog(dialog));
    handleMenuClose();
  };

  const handleDuplicate = () => {
    const copyName = t('projects.copyOf', { name: project.name });
    const duplicate = duplicateProject(project, copyName);
    dispatch(projectSlice.actions.onAddProject(duplicate));
    handleMenuClose();
  };

  const handleDownload = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    downloadProjectAsBpsFile(project);
    handleMenuClose();
  };

  const handleDelete = () => {
    dispatch(projectSlice.actions.onSelectProjects([project]));
    dispatch(projectSlice.actions.onChangeMultiSelect(false));
    dispatch(applicationSlice.actions.onOpenDialog(DELETE_PROJECT));
    handleMenuClose();
  };

  const handleCardClick = () => {
    if (!anchorEl) {
      dispatch(projectSlice.actions.onLoadProject(project));
      window.projectId = project.id;

      if (!project.selectedTabs?.dashboardTab) {
        dispatch(projectSlice.actions.onSelectDashboardTab(DRIVEBASE_DATA));
      }

      // Navigate to editor page (default page when opening a project)
      navigate(`/editor/${project.id}`);
    }
  };

  const handleCardSelect = (e: React.MouseEvent) => {
    if (e.shiftKey) {
      handleShiftProjectSelect(e);
    } else {
      handleProjectSelect();
    }
  };

  const handleOpenShareCodeInfoDialog = () => {
    dispatch(
      applicationSlice.actions.onOpenDialog({
        ...ApplicationActions.Dialogs.SHARE_CODE_INFO,
        options: { ...ApplicationActions.Dialogs.SHARE_CODE_INFO.options, project: project, newShareCode: false },
      })
    );
    handleMenuClose();
  };

  const handleOpenCreateShareCodeDialog = () => {
    dispatch(
      applicationSlice.actions.onOpenDialog({
        ...ApplicationActions.Dialogs.CREATE_SHARE_CODE,
        options: { ...ApplicationActions.Dialogs.CREATE_SHARE_CODE.options, project: project },
      })
    );
    handleMenuClose();
  };

  const getRightIcon = () => {
    if (isMultiSelect) return <Checkbox checked={isSelected} sx={{ p: 0 }} />;
    return (
      <IconButton
        aria-label={t('aria-label.more')}
        aria-haspopup="true"
        onClick={(event) => {
          event.stopPropagation();
          handleMenuClick(event);
        }}
        sx={{ p: 0 }}
      >
        <MoreVertIcon />
      </IconButton>
    );
  };

  return (
    <>
      <Card
        sx={{
          width: '320px',
          height: '80px',
          flexDirection: 'row',
          alignItems: 'center',
          cursor: 'pointer',
          boxSizing: 'border-box',
          margin: 'auto',
          position: 'relative',
        }}
        onClick={isMultiSelect ? handleCardSelect : handleCardClick}
      >
        {project.isCollaborative && project.collaborators && project.collaborators.length > 0 ? (
          <Tooltip
            title={t('projectCard.tooltip.collaborating', {
              count: project.collaborators.length,
              defaultValue: `Collaborating with ${project.collaborators.length} other${project.collaborators.length === 1 ? '' : 's'}`,
            })}
          >
            <PeopleIcon
              sx={{
                position: 'absolute',
                left: 2,
                top: 2,
                color: 'success.main',
              }}
            />
          </Tooltip>
        ) : project.shareCode ? (
          <Tooltip title={t('projectCard.tooltip.shared', 'Shared project')}>
            <ShareCodeInfo
              sx={{
                position: 'absolute',
                left: 2,
                top: 2,
                color: 'info.main',
              }}
            />
          </Tooltip>
        ) : null}
        <Stack direction="row" justifyContent="space-between" sx={{ width: '100%', alignItems: 'center' }}>
          <Typography
            variant="panelTitle"
            sx={{
              wordWrap: 'break-word',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              WebkitLineClamp: 2,
              flexGrow: 1,
              padding: '0 12px',
            }}
          >
            {project.name}
          </Typography>
          {getRightIcon()}
        </Stack>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          slotProps={{
            paper: {
              style: {
                borderRadius: '8px',
                width: '30ch',
                boxShadow: '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
              },
            },
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {project.shareCode ? (
            <MenuItem onClick={handleOpenShareCodeInfoDialog}>
              <ListItemIcon>
                <ShareCodeInfo />
              </ListItemIcon>
              <Trans i18nKey="projectCard.menu.share-code-info">Share Code Info</Trans>
            </MenuItem>
          ) : (
            <MenuItem onClick={handleOpenCreateShareCodeDialog}>
              <ListItemIcon>
                <ShareCodeUpload />
              </ListItemIcon>
              <Trans i18nKey="projectCard.menu.create-share-code">Create Share Code</Trans>
            </MenuItem>
          )}

          <MenuItem onClick={handleOpenEditProgramDialog}>
            <ListItemIcon>
              <ModeEditIcon />
            </ListItemIcon>
            <Trans i18nKey="projectCard.menu.edit">Edit</Trans>
          </MenuItem>

          <MenuItem onClick={handleDuplicate}>
            <ListItemIcon>
              <ContentCopyIcon />
            </ListItemIcon>
            <Trans i18nKey="projectCard.menu.duplicate">Duplicate</Trans>
          </MenuItem>
          <MenuItem onClick={handleDownload}>
            <ListItemIcon>
              <DownloadIcon />
            </ListItemIcon>
            <Trans i18nKey="projectCard.menu.download">Download</Trans>
          </MenuItem>
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            <Trans i18nKey="projectCard.menu.delete">Delete</Trans>
          </MenuItem>
        </Menu>
      </Card>
    </>
  );
};

export default ProjectCard;
